/**
 * ChatFiltersBar - Barra de filtros com chips para o chat
 * Segue o padrão LeadsAdvancedFilters (Linear/Attio style)
 *
 * A busca por texto de conversas fica integrada no popover.
 * Quando aplicada, gera um chip "Termo: xxx".
 */

import React, { useState, useEffect, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { FilterChip } from '@/shared/components/ui/FilterChip';
import {
  CONVERSATION_FILTER_DEFINITIONS,
  CONVERSATION_FILTER_CATEGORIES,
  CONTACT_FILTER_DEFINITIONS,
  CONTACT_FILTER_CATEGORIES,
  GROUPS_FILTER_DEFINITIONS,
  GROUPS_FILTER_CATEGORIES,
  getActiveFilters,
  type ChatFiltersState,
  type FilterOption,
} from './ChatFilterDefinitions';
import { useTeamsDropdown } from '@/modules/teams/hooks/useTeams';
import { useUsersDropdown } from '@/modules/users/hooks/useUsers';
import { useChannelsDropdown } from '@/modules/channels/hooks/useChannelsDropdown';
import { useTagCatalog } from '@/shared/hooks/useTagCatalog';
import { useCustomersDropdown } from '@/modules/crm/customers/hooks';
import { useFunnelsDropdown, useStepsByFunnelId } from '@/modules/funnels/hooks';
import type { SidebarView } from '../types';
import { Icon } from '@/shared/icons'
// ═══════════════════════════════════════════════════════
// PROPS
// ═══════════════════════════════════════════════════════

export interface ChatFiltersBarProps {
  /** View type para determinar quais filtros mostrar */
  viewType: SidebarView;
  /** Filtros ativos */
  filters: ChatFiltersState;
  /** Callback quando filtros mudam */
  onFiltersChange: (filters: ChatFiltersState) => void;
  /** Se deve mostrar filtro de operador */
  showOperatorFilter?: boolean;
  /**
   * Ref externo do botão trigger (ex: botão no header).
   * Quando fornecido, o popover é posicionado relativo a esse elemento
   * e o botão interno "+ Filtro" NÃO é renderizado.
   */
  externalTriggerRef?: React.RefObject<HTMLButtonElement | null>;
  /** Controle externo de abertura do popover */
  isPopoverOpen?: boolean;
  /** Callback quando popover fecha */
  onPopoverClose?: () => void;
}

// ═══════════════════════════════════════════════════════
// FUNNEL-STEPS SELECTOR (drill-down funil → etapas)
// Replica o visual do ConversationStageSelector do header, mas em modo
// multiseleção: acumula stepIds em `selected` (pode misturar funis).
// ═══════════════════════════════════════════════════════

const FUNNEL_PURPLE = '#8b5cf6';

const FunnelDot: React.FC<{ color: string }> = ({ color }) => (
  <span style={{ width: 9, height: 9, borderRadius: '50%', backgroundColor: color, flex: '0 0 auto' }} />
);

const FunnelChevronRight: React.FC = () => (
  <svg width={13} height={13} viewBox="0 0 24 24" fill="none" style={{ flex: '0 0 auto' }}>
    <path d="M9 6l6 6-6 6" stroke="#94a3b8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

interface FunnelStepsListProps {
  funnelId: string;
  funnelName?: string;
  funnelColor?: string;
  selected: string[];
  onToggle: (stepId: string) => void;
}

/** Nível 2: etapas de um funil (lazy — só busca ao entrar no funil). */
const FunnelStepsList: React.FC<FunnelStepsListProps> = ({ funnelId, funnelName, funnelColor, selected, onToggle }) => {
  const { data: steps = [], isLoading } = useStepsByFunnelId(funnelId);
  const ordered = useMemo(() => [...steps].sort((a, b) => (a.order ?? 0) - (b.order ?? 0)), [steps]);

  return (
    <div className="p-2 space-y-0.5">
      <div className="flex items-center gap-2 px-1 pb-1 text-[11px]" style={{ color: '#9ca3af' }}>
        {funnelColor && <FunnelDot color={funnelColor} />}
        <span className="truncate">{funnelName || 'Funil'}</span>
      </div>
      {isLoading && (
        <div className="text-center py-3 text-sm" style={{ color: '#9ca3af' }}>Carregando etapas…</div>
      )}
      {!isLoading && ordered.length === 0 && (
        <div className="text-center py-3 text-sm" style={{ color: '#9ca3af' }}>Sem etapas</div>
      )}
      {ordered.map((s) => {
        const isSelected = selected.includes(s.id);
        return (
          <button
            key={s.id}
            type="button"
            onClick={() => onToggle(s.id)}
            className="w-full flex items-center justify-between px-3 py-2 rounded text-sm transition-colors text-left"
            style={{ backgroundColor: isSelected ? '#f5f3ff' : 'transparent', color: '#1e293b' }}
            data-testid={`filter-stage-option-${s.id}`}
            onMouseEnter={(e) => { if (!isSelected) e.currentTarget.style.backgroundColor = '#f9fafb'; }}
            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = isSelected ? '#f5f3ff' : 'transparent'; }}
          >
            <span className="flex items-center gap-2.5 min-w-0">
              <FunnelDot color={s.color || FUNNEL_PURPLE} />
              <span className="truncate">{s.name}</span>
            </span>
            {isSelected && (
              <div className="w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#8b5cf6' }}>
                <Icon name="check" className="w-3 h-3 text-white" />
              </div>
            )}
          </button>
        );
      })}
    </div>
  );
};

interface FunnelStepsSelectorProps {
  /** stepIds selecionados (= localValues do popover) */
  selected: string[];
  onToggle: (stepId: string) => void;
}

/** Nível 1: lista de funis → drill numa etapa. Estado interno = funil aberto. */
const FunnelStepsSelector: React.FC<FunnelStepsSelectorProps> = ({ selected, onToggle }) => {
  const { data: funnels = [] } = useFunnelsDropdown();
  const [drillFunnelId, setDrillFunnelId] = useState<string | null>(null);

  const sortedFunnels = useMemo(
    () => [...funnels].sort((a, b) => (a.order ?? 0) - (b.order ?? 0)),
    [funnels]
  );

  if (drillFunnelId !== null) {
    const current = sortedFunnels.find((f) => f.id === drillFunnelId);
    return (
      <div>
        <button
          type="button"
          onClick={() => setDrillFunnelId(null)}
          className="flex items-center gap-1.5 w-full px-3 py-2 text-xs transition-colors"
          style={{ color: '#64748b' }}
          data-testid="filter-funnel-back"
          onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#f1f5f9'; }}
          onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
        >
          <Icon name="chevron-left" className="w-3.5 h-3.5" />
          <span>voltar aos funis</span>
        </button>
        <FunnelStepsList
          funnelId={drillFunnelId}
          funnelName={current?.name}
          funnelColor={current?.color}
          selected={selected}
          onToggle={onToggle}
        />
      </div>
    );
  }

  return (
    <div className="p-2 space-y-0.5">
      {selected.length > 0 && (
        <div className="px-1 pb-1 text-[11px]" style={{ color: '#9ca3af' }}>
          {selected.length} etapa{selected.length !== 1 ? 's' : ''} selecionada{selected.length !== 1 ? 's' : ''}
        </div>
      )}
      {sortedFunnels.length === 0 ? (
        <div className="text-center py-4 text-sm" style={{ color: '#9ca3af' }}>Nenhum funil disponível</div>
      ) : (
        sortedFunnels.map((f) => (
          <button
            key={f.id}
            type="button"
            onClick={() => setDrillFunnelId(f.id)}
            className="w-full flex items-center gap-2.5 px-3 py-2 rounded text-sm transition-colors text-left"
            style={{ color: '#1e293b' }}
            data-testid={`filter-funnel-option-${f.id}`}
            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#f9fafb'; }}
            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
          >
            <FunnelDot color={f.color || FUNNEL_PURPLE} />
            <span className="flex-1 min-w-0 truncate">{f.name}</span>
            <FunnelChevronRight />
          </button>
        ))
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════════
// COMPONENT
// ═══════════════════════════════════════════════════════

export const ChatFiltersBar: React.FC<ChatFiltersBarProps> = ({
  viewType,
  filters,
  onFiltersChange,
  showOperatorFilter = true,
  externalTriggerRef,
  isPopoverOpen,
  onPopoverClose,
}) => {
  const hasExternalTrigger = !!externalTriggerRef;

  // ── Popover state ──
  // O popover abre por trigger externo (isPopoverOpen) OU por clique em chip/botão interno
  const [internalOpen, setInternalOpen] = useState(false);
  const isOpen = internalOpen || (isPopoverOpen ?? false);
  const [step, setStep] = useState<'categories' | 'values'>('categories');
  const [selectedFilter, setSelectedFilter] = useState<string | null>(null);
  const [optionSearchQuery, setOptionSearchQuery] = useState('');

  // ── Search is now in the sidebar header (always visible) ──

  // ── Local values for value selector ──
  const [localValues, setLocalValues] = useState<string[]>([]);
  const [localDateFrom, setLocalDateFrom] = useState('');
  const [localDateTo, setLocalDateTo] = useState('');
  const [localToggle, setLocalToggle] = useState(false);

  // ── Positioning ──
  const internalTriggerRef = useRef<HTMLButtonElement>(null);
  const triggerRef = hasExternalTrigger ? externalTriggerRef : internalTriggerRef;
  const [popoverPosition, setPopoverPosition] = useState({ top: 0, left: 0 });

  // ── Dynamic data ──
  const { data: teamsData } = useTeamsDropdown();
  const { data: usersData } = useUsersDropdown({ status: 'active' });
  const { data: channelsData } = useChannelsDropdown();
  // Catálogo de tags (collection `tags`, desde 22/09/2026) — e não mais o
  // `distinct` sobre contatos. Importa para o filtro: tag recém-cadastrada
  // aparece aqui mesmo antes de alguém ter sido marcado com ela.
  const { data: tagsData } = useTagCatalog();
  // Não gatear por isOpen: os chips ativos precisam resolver nome do cliente mesmo com o popover fechado.
  const { data: customersData } = useCustomersDropdown();

  // ── Determine definitions and categories based on view ──
  const isContactView = viewType === 'contacts';
  const isGroupsView = viewType === 'groups';

  const filterDefinitions = useMemo(() => {
    const defs = isContactView
      ? { ...CONTACT_FILTER_DEFINITIONS }
      : isGroupsView
        ? { ...GROUPS_FILTER_DEFINITIONS }
        : { ...CONVERSATION_FILTER_DEFINITIONS };

    // Remove operator filter if not allowed
    if (!showOperatorFilter && defs.assigneeId) {
      delete defs.assigneeId;
    }

    return defs;
  }, [isContactView, isGroupsView, showOperatorFilter]);

  const filterCategories = useMemo(() => {
    const cats = isContactView
      ? CONTACT_FILTER_CATEGORIES
      : isGroupsView
        ? GROUPS_FILTER_CATEGORIES
        : CONVERSATION_FILTER_CATEGORIES;

    // Filter out categories that have no filters after removing assigneeId
    return cats
      .map(cat => ({
        ...cat,
        filters: cat.filters.filter(fKey => filterDefinitions[fKey]),
      }))
      .filter(cat => cat.filters.length > 0);
  }, [isContactView, isGroupsView, filterDefinitions]);

  // ── Dynamic options mapping ──
  const dynamicOptions = useMemo<Record<string, FilterOption[]>>(() => {
    const opts: Record<string, FilterOption[]> = {};

    if (teamsData) {
      opts.teamId = teamsData.map((t: any) => ({
        value: t.id,
        label: t.name,
      }));
    }

    if (usersData) {
      opts.assigneeId = usersData.map((u: any) => ({
        value: u.id,
        label: u.name || `${u.firstName || ''} ${u.lastName || ''}`.trim() || u.email,
      }));
    }

    if (channelsData) {
      opts.channelId = channelsData.map((c: any) => ({
        value: c.id,
        label: c.name,
      }));
    }

    if (tagsData) {
      opts.tags = tagsData.map((tag) => ({
        value: tag.name,
        label: tag.name,
      }));
    }

    if (customersData) {
      opts.contactCustomerId = customersData.map((c) => ({
        value: c.id,
        label: c.name,
      }));
    }

    return opts;
  }, [teamsData, usersData, channelsData, tagsData, customersData]);

  // ── Active filters (includes search chip) ──
  const activeFilters = useMemo(
    () => getActiveFilters(filters, filterDefinitions, dynamicOptions),
    [filters, filterDefinitions, dynamicOptions]
  );
  const hasFilters = activeFilters.length > 0;

  const currentFilterDef = selectedFilter ? filterDefinitions[selectedFilter] : null;

  // ── Update popover position (clamp na viewport — painel w-72 = 288px;
  // no mobile o trigger fica na borda direita e vazava pra fora da tela) ──
  useEffect(() => {
    if (isOpen && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      const PANEL_W = 288;
      const left = Math.max(8, Math.min(rect.left + window.scrollX, window.innerWidth - PANEL_W - 8));
      setPopoverPosition({
        top: rect.bottom + window.scrollY + 4,
        left,
      });
    }
  }, [isOpen]);

  // ── Filtered options for multiselect ──
  const filteredOptions = useMemo(() => {
    if (!currentFilterDef) return [];

    const options = currentFilterDef.dynamic && dynamicOptions[currentFilterDef.key]
      ? dynamicOptions[currentFilterDef.key]
      : currentFilterDef.options || [];

    if (!optionSearchQuery.trim()) return options;
    const query = optionSearchQuery.toLowerCase();
    return options.filter(o => o.label.toLowerCase().includes(query));
  }, [currentFilterDef, optionSearchQuery, dynamicOptions]);

  // ── Reset popover state when opening externally ──
  useEffect(() => {
    if (hasExternalTrigger && isPopoverOpen) {
      setStep('categories');
      setSelectedFilter(null);
      setOptionSearchQuery('');
      setLocalValues([]);
      setLocalDateFrom('');
      setLocalDateTo('');
      setLocalToggle(false);
    }
  }, [isPopoverOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Handlers ──
  const handleOpenAdd = () => {
    setStep('categories');
    setSelectedFilter(null);
    setOptionSearchQuery('');
    setLocalValues([]);
    setLocalDateFrom('');
    setLocalDateTo('');
    setLocalToggle(false);
    setInternalOpen(true);
  };

  const handleEditChip = (filterKey: string) => {
    setStep('values');
    setSelectedFilter(filterKey);
    setOptionSearchQuery('');

    if (filterKey === 'dateRange') {
      setLocalDateFrom(filters.dateFrom || '');
      setLocalDateTo(filters.dateTo || '');
    } else if (filterKey === 'hasUnreadMessages') {
      setLocalToggle(!!filters.hasUnreadMessages);
    } else {
      const value = filters[filterKey as keyof ChatFiltersState];
      setLocalValues(Array.isArray(value) ? [...value] : []);
    }

    setInternalOpen(true);
  };

  const handleSelectCategory = (filterKey: string) => {
    setSelectedFilter(filterKey);
    setOptionSearchQuery('');

    if (filterKey === 'dateRange') {
      setLocalDateFrom(filters.dateFrom || '');
      setLocalDateTo(filters.dateTo || '');
    } else if (filterKey === 'hasUnreadMessages') {
      setLocalToggle(!!filters.hasUnreadMessages);
    } else {
      const value = filters[filterKey as keyof ChatFiltersState];
      setLocalValues(Array.isArray(value) ? [...value] : []);
    }

    setStep('values');
  };

  const handleApply = () => {
    if (!selectedFilter) return;
    const newFilters = { ...filters };

    if (selectedFilter === 'dateRange') {
      if (localDateFrom) newFilters.dateFrom = localDateFrom;
      else delete newFilters.dateFrom;
      if (localDateTo) newFilters.dateTo = localDateTo;
      else delete newFilters.dateTo;
    } else if (selectedFilter === 'hasUnreadMessages') {
      if (localToggle) newFilters.hasUnreadMessages = true;
      else delete newFilters.hasUnreadMessages;
    } else {
      if (localValues.length === 0) {
        delete newFilters[selectedFilter as keyof ChatFiltersState];
      } else {
        (newFilters as Record<string, unknown>)[selectedFilter] = localValues;
      }
    }

    onFiltersChange(newFilters);
    handleClose();
  };

  const handleRemoveChip = (filterKey: string) => {
    const newFilters = { ...filters };
    if (filterKey === 'dateRange') {
      delete newFilters.dateFrom;
      delete newFilters.dateTo;
    } else {
      delete newFilters[filterKey as keyof ChatFiltersState];
    }
    onFiltersChange(newFilters);
  };

  const handleClearAll = () => {
    onFiltersChange({});
  };

  const handleToggleOption = (value: string) => {
    if (localValues.includes(value)) {
      setLocalValues(localValues.filter(v => v !== value));
    } else {
      setLocalValues([...localValues, value]);
    }
  };

  const handleClose = () => {
    setInternalOpen(false);
    if (onPopoverClose) onPopoverClose();
  };

  const handleBack = () => {
    setStep('categories');
    setSelectedFilter(null);
  };

  const isFilterActive = (filterKey: string): boolean => {
    if (filterKey === 'dateRange') return !!(filters.dateFrom || filters.dateTo);
    if (filterKey === 'hasUnreadMessages') return !!filters.hasUnreadMessages;
    const value = filters[filterKey as keyof ChatFiltersState];
    if (!value) return false;
    if (Array.isArray(value) && value.length === 0) return false;
    return true;
  };

  // Determina se há conteúdo visível na barra (chips ativos)
  const hasVisibleContent = hasFilters;

  return (
    <>
    {/* ═══ Chips bar (só renderiza se há conteúdo) ═══ */}
    {hasVisibleContent && (
    <div className="flex items-center gap-1.5 flex-wrap mt-2">
      {/* ═══ Active Filter Chips (inclui "Termo: xxx") ═══ */}
      {activeFilters.map((filter) => (
        <FilterChip
          key={filter.key}
          label={filter.label}
          value={filter.displayValue}
          onClick={() => handleEditChip(filter.key)}
          onRemove={() => handleRemoveChip(filter.key)}
        />
      ))}

      {/* ═══ Add Filter / Search Button (only when no external trigger) ═══ */}
      {!hasExternalTrigger && (
        <button
          ref={internalTriggerRef}
          type="button"
          onClick={handleOpenAdd}
          className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-all duration-200"
          style={{
            color: 'var(--theme-muted-text)',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = 'var(--theme-button-ghost-hover)';
            e.currentTarget.style.color = 'var(--theme-header-text)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'transparent';
            e.currentTarget.style.color = 'var(--theme-muted-text)';
          }}
        >
          <Icon name="plus" className="w-3.5 h-3.5" />
          <span>Filtro</span>
        </button>
      )}

      {/* ═══ Clear All ═══ */}
      {hasFilters && (
        <button
          type="button"
          onClick={handleClearAll}
          className="text-xs font-medium px-2 py-1.5 rounded transition-colors"
          style={{ color: 'var(--theme-muted-text)' }}
          onMouseEnter={(e) => {
            e.currentTarget.style.color = 'var(--theme-header-text)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.color = 'var(--theme-muted-text)';
          }}
        >
          Limpar
        </button>
      )}
    </div>
    )}

      {/* ═══ Popover (always-light, via portal) ═══ */}
      {isOpen &&
        createPortal(
          <>
            {/* Backdrop */}
            <div className="fixed inset-0 z-[100]" onClick={handleClose} />

            {/* Panel */}
            <div
              className="fixed z-[101] w-72 rounded-lg shadow-xl flex flex-col"
              style={{
                top: `${popoverPosition.top}px`,
                left: `${popoverPosition.left}px`,
                backgroundColor: '#ffffff',
                border: '1px solid #e5e7eb',
                maxHeight: '420px',
              }}
            >
              {step === 'categories' ? (
                <>
                  {/* ═══ Filter Categories ═══ */}
                  <div className="overflow-y-auto flex-1 py-1">
                    {filterCategories.map((category, catIndex) => (
                      <div key={category.id}>
                        <div
                          className="flex items-center gap-2 px-3 py-1.5"
                          style={{
                            color: '#9ca3af',
                            ...(catIndex > 0
                              ? { borderTop: '1px solid #f3f4f6', marginTop: '4px', paddingTop: '8px' }
                              : {}),
                          }}
                        >
                          <span className="text-[11px] font-semibold uppercase tracking-wider">
                            {category.label}
                          </span>
                        </div>

                        {category.filters.map((filterKey) => {
                          const def = filterDefinitions[filterKey];
                          if (!def) return null;
                          const active = isFilterActive(filterKey);

                          return (
                            <button
                              key={filterKey}
                              type="button"
                              onClick={() => handleSelectCategory(filterKey)}
                              className="w-full flex items-center justify-between py-2 text-sm transition-colors text-left"
                              style={{ paddingLeft: '2rem', paddingRight: '0.75rem', color: '#1e293b' }}
                              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#f9fafb'; }}
                              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                            >
                              <span>{def.label}</span>
                              {active && (
                                <div
                                  className="w-1.5 h-1.5 rounded-full flex-shrink-0"
                                  style={{ backgroundColor: '#8b5cf6' }}
                                />
                              )}
                            </button>
                          );
                        })}
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <>
                  {/* Header with back button */}
                  <div
                    className="flex items-center gap-2 px-3 py-2.5 flex-shrink-0"
                    style={{ borderBottom: '1px solid #e5e7eb' }}
                  >
                    <button
                      type="button"
                      onClick={handleBack}
                      className="p-1 rounded transition-colors"
                      style={{ color: '#64748b' }}
                      onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#f1f5f9'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                    >
                      <Icon name="chevron-left" className="w-4 h-4" />
                    </button>
                    <span className="text-sm font-semibold" style={{ color: '#1e293b' }}>
                      {currentFilterDef?.label}
                    </span>
                  </div>

                  {/* Value Content */}
                  <div className="overflow-y-auto flex-1">
                    {/* ── MultiSelect ── */}
                    {currentFilterDef?.type === 'multiselect' && (
                      <>
                        {filteredOptions.length > 5 && (
                          <div className="px-3 pt-3 pb-1">
                            <div className="relative">
                              <Icon name="search" className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5"
                                style={{ color: '#9ca3af' }} />
                              <input
                                type="text"
                                value={optionSearchQuery}
                                onChange={(e) => setOptionSearchQuery(e.target.value)}
                                placeholder={currentFilterDef.placeholder || 'Buscar...'}
                                className="w-full h-[38px] pl-8 pr-3 py-1.5 text-sm rounded-md border"
                                style={{ backgroundColor: '#f9fafb', borderColor: '#e5e7eb', color: '#1e293b' }}
                                autoFocus
                              />
                            </div>
                          </div>
                        )}

                        <div className="p-2 space-y-0.5">
                          {filteredOptions.length === 0 ? (
                            <div className="text-center py-4 text-sm" style={{ color: '#9ca3af' }}>
                              Nenhuma opção encontrada
                            </div>
                          ) : (
                            filteredOptions.map((option) => {
                              const isSelected = localValues.includes(option.value);
                              return (
                                <button
                                  key={option.value}
                                  type="button"
                                  onClick={() => handleToggleOption(option.value)}
                                  className="w-full flex items-center justify-between px-3 py-2 rounded text-sm transition-colors text-left"
                                  style={{
                                    backgroundColor: isSelected ? '#f5f3ff' : 'transparent',
                                    color: '#1e293b',
                                  }}
                                  onMouseEnter={(e) => {
                                    if (!isSelected) e.currentTarget.style.backgroundColor = '#f9fafb';
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor = isSelected ? '#f5f3ff' : 'transparent';
                                  }}
                                >
                                  <span>{option.label}</span>
                                  {isSelected && (
                                    <div
                                      className="w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0"
                                      style={{ backgroundColor: '#8b5cf6' }}
                                    >
                                      <Icon name="check" className="w-3 h-3 text-white" />
                                    </div>
                                  )}
                                </button>
                              );
                            })
                          )}
                        </div>

                        {localValues.length > 0 && (
                          <div className="px-3 py-1.5 text-[11px]" style={{ color: '#9ca3af' }}>
                            {localValues.length} selecionado{localValues.length !== 1 ? 's' : ''}
                          </div>
                        )}
                      </>
                    )}

                    {/* ── Toggle ── */}
                    {currentFilterDef?.type === 'toggle' && (
                      <div className="p-3">
                        <label className="flex items-center gap-3 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={localToggle}
                            onChange={(e) => setLocalToggle(e.target.checked)}
                            className="w-4 h-4 rounded"
                            style={{ accentColor: '#8b5cf6' }}
                          />
                          <span className="text-sm" style={{ color: '#1e293b' }}>
                            Mostrar apenas não lidas
                          </span>
                        </label>
                      </div>
                    )}

                    {/* ── Date Range ── */}
                    {currentFilterDef?.type === 'daterange' && (
                      <div className="p-3 space-y-3">
                        <div>
                          <label className="block text-xs font-medium mb-1" style={{ color: '#64748b' }}>
                            De
                          </label>
                          <input
                            type="date"
                            value={localDateFrom}
                            onChange={(e) => setLocalDateFrom(e.target.value)}
                            className="w-full h-[38px] px-3 py-2 text-sm rounded-md border"
                            style={{ backgroundColor: '#ffffff', borderColor: '#d1d5db', color: '#1e293b' }}
                            onFocus={(e) => { e.currentTarget.style.borderColor = '#8b5cf6'; }}
                            onBlur={(e) => { e.currentTarget.style.borderColor = '#d1d5db'; }}
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium mb-1" style={{ color: '#64748b' }}>
                            Até
                          </label>
                          <input
                            type="date"
                            value={localDateTo}
                            onChange={(e) => setLocalDateTo(e.target.value)}
                            className="w-full h-[38px] px-3 py-2 text-sm rounded-md border"
                            style={{ backgroundColor: '#ffffff', borderColor: '#d1d5db', color: '#1e293b' }}
                            onFocus={(e) => { e.currentTarget.style.borderColor = '#8b5cf6'; }}
                            onBlur={(e) => { e.currentTarget.style.borderColor = '#d1d5db'; }}
                          />
                        </div>
                      </div>
                    )}

                    {/* ── Funnel Steps (drill-down funil → etapas) ── */}
                    {currentFilterDef?.type === 'funnel-steps' && (
                      <FunnelStepsSelector selected={localValues} onToggle={handleToggleOption} />
                    )}
                  </div>

                  {/* Apply Footer */}
                  <div
                    className="px-3 py-2.5 flex items-center justify-end flex-shrink-0"
                    style={{ borderTop: '1px solid #e5e7eb' }}
                  >
                    <button
                      type="button"
                      onClick={handleApply}
                      className="px-4 py-1.5 text-sm font-medium rounded-md transition-colors"
                      style={{ backgroundColor: '#8b5cf6', color: '#ffffff' }}
                      onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#7c3aed'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#8b5cf6'; }}
                    >
                      Aplicar
                    </button>
                  </div>
                </>
              )}
            </div>
          </>,
          document.body
        )}
    </>
  );
};

ChatFiltersBar.displayName = 'ChatFiltersBar';
